/**
 * Project Detail Page
 *
 * Displays project details with units and reservation functionality.
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchProjectById, followProject, unfollowProject, isFollowingProject, getFollowerCount } from '@/features/projects/api/projectsApi'
import { reserveUnit, fetchMyReservations, hasPreviousWithdrawals } from '@/features/reservations/api/reservationsApi'
import { useAuth } from '@/features/auth/AuthContext'
import { supabase } from '@/lib/supabaseClient'
import { useAnalytics } from '@/lib/analytics'
import ImageCarousel from '@/components/ImageCarousel'
import ProjectMap from '@/components/ProjectMap'
import DiscountBanner from '@/features/discounts/components/DiscountBanner'
import ProjectQA from '@/features/communication/components/ProjectQA'
import ProjectTimeline from '@/features/milestones/components/ProjectTimeline'
import BottomSheet from '@/components/mobile/BottomSheet'
import BottomSheetHeader from '@/components/mobile/BottomSheetHeader'
import SwipeableImageGallery from '@/components/mobile/SwipeableImageGallery'
import ProjectFollowButton from '@/components/ProjectFollowButton'
import DiscountBadge from '@/components/DiscountBadge'
import { getCurrentDiscount, updateDiscountAllocation } from '@/features/discounts/api/discountsApi'

export default function ProjectDetailPage() {
  const { projectId } = useParams()
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { trackProjectView, trackProjectFollow, trackProjectUnfollow, trackUnitReserve } = useAnalytics()

  const [selectedUnit, setSelectedUnit] = useState(null)
  const [reservationNotes, setReservationNotes] = useState('')
  const [showReserveModal, setShowReserveModal] = useState(false)
  const [statusFilter, setStatusFilter] = useState('available')
  const [discountAllocation, setDiscountAllocation] = useState({
    priceReduction: 0,
    depositContribution: 0,
    furniturePackages: 0,
  })

  // Fetch project details
  const { data: projectData, isLoading, error } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => fetchProjectById(projectId),
  })

  // Fetch project images
  const { data: projectImagesData, isLoading: imagesLoading } = useQuery({
    queryKey: ['project-images', projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_images')
        .select('*')
        .eq('project_id', projectId)
        .order('display_order', { ascending: true })

      if (error) throw error
      return data || []
    },
    enabled: !!projectId,
  })

  // Check if following
  const { data: isFollowing } = useQuery({
    queryKey: ['following', projectId],
    queryFn: () => isFollowingProject(projectId),
    enabled: !!user && profile?.role === 'buyer',
  })

  // Fetch follower count for social proof
  const { data: followerCountData } = useQuery({
    queryKey: ['follower-count', projectId],
    queryFn: () => getFollowerCount(projectId),
    refetchInterval: 30000,
  })

  // Fetch buyer's active reservations to show "Reserved" badges on units
  const { data: reservationsData } = useQuery({
    queryKey: ['my-active-reservations'],
    queryFn: () => fetchMyReservations('active'),
    enabled: !!user && profile?.role === 'buyer',
  })

  const reservedUnitIds = new Set(
    reservationsData?.data?.map(r => r.unit?.id).filter(Boolean) || []
  )

  // Check if buyer has previous withdrawals (affects reservation duration)
  const { data: hasWithdrawalsData } = useQuery({
    queryKey: ['has-previous-withdrawals'],
    queryFn: () => hasPreviousWithdrawals(),
    enabled: !!user && profile?.role === 'buyer',
  })

  const hasWithdrawals = hasWithdrawalsData?.data || false
  const reservationDuration = hasWithdrawals ? 48 : 72

  // Fetch current discount for this project
  const { data: currentDiscountData } = useQuery({
    queryKey: ['current-discount', projectId],
    queryFn: () => getCurrentDiscount(projectId),
  })

  // Fetch unit eligibility for discounts
  const { data: unitEligibilityData } = useQuery({
    queryKey: ['unit-eligibility', projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('unit_discount_eligibility')
        .select('unit_id, is_eligible')
        .in('unit_id', projectData?.data?.units?.map(u => u.id) || [])

      if (error) throw error

      const eligibilityMap = new Map()
      data?.forEach(e => eligibilityMap.set(e.unit_id, e.is_eligible))
      return eligibilityMap
    },
    enabled: !!projectData?.data?.units?.length,
  })

  const currentDiscount = currentDiscountData?.data
  const unitEligibility = unitEligibilityData || new Map()

  // Track project view
  useEffect(() => {
    if (projectId) {
      trackProjectView(projectId)
    }
  }, [projectId])

  // Follow mutation
  const followMutation = useMutation({
    mutationFn: () => followProject(projectId),
    onMutate: async () => {
      await queryClient.cancelQueries(['following', projectId])
      const previousFollowing = queryClient.getQueryData(['following', projectId])
      queryClient.setQueryData(['following', projectId], { data: true })
      return { previousFollowing }
    },
    onError: (err, variables, context) => {
      if (context?.previousFollowing) {
        queryClient.setQueryData(['following', projectId], context.previousFollowing)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['following', projectId])
      queryClient.invalidateQueries(['follower-count', projectId])
      trackProjectFollow(projectId)
    },
  })

  // Unfollow mutation
  const unfollowMutation = useMutation({
    mutationFn: () => unfollowProject(projectId),
    onMutate: async () => {
      await queryClient.cancelQueries(['following', projectId])
      const previousFollowing = queryClient.getQueryData(['following', projectId])
      queryClient.setQueryData(['following', projectId], { data: false })
      return { previousFollowing }
    },
    onError: (err, variables, context) => {
      if (context?.previousFollowing) {
        queryClient.setQueryData(['following', projectId], context.previousFollowing)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['following', projectId])
      queryClient.invalidateQueries(['follower-count', projectId])
      trackProjectUnfollow(projectId)
    },
  })

  // Reserve unit mutation
  const reserveMutation = useMutation({
    mutationFn: async ({ unitId, notes, allocation }) => {
      // Reserve the unit
      const reserveResult = await reserveUnit(unitId, notes)
      if (reserveResult.error) throw reserveResult.error

      // If there's a discount allocation, update it
      if (allocation && (allocation.priceReduction > 0 || allocation.depositContribution > 0 || allocation.furniturePackages > 0)) {
        // Get the reservation discount ID
        const { data: reservationDiscount, error: discountError } = await supabase
          .from('reservation_discounts')
          .select('id')
          .eq('reservation_id', reserveResult.data.id)
          .maybeSingle()

        if (!discountError && reservationDiscount) {
          // Update the allocation
          await updateDiscountAllocation(reservationDiscount.id, allocation)
        }
      }

      return reserveResult
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['project', projectId])
      queryClient.invalidateQueries(['my-active-reservations'])
      trackUnitReserve(variables.unitId, projectId)
      setShowReserveModal(false)
      setReservationNotes('')
      setDiscountAllocation({
        priceReduction: 0,
        depositContribution: 0,
        furniturePackages: 0,
      })
      navigate('/dashboard/buyer')
    },
  })

  // Real-time subscriptions for project and units updates
  useEffect(() => {
    if (!projectId) return

    const projectChannel = supabase
      .channel(`project:${projectId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'projects',
          filter: `id=eq.${projectId}`,
        },
        () => {
          queryClient.invalidateQueries(['project', projectId])
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'units',
          filter: `project_id=eq.${projectId}`,
        },
        () => {
          queryClient.invalidateQueries(['project', projectId])
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(projectChannel)
    }
  }, [projectId, queryClient])

  const project = projectData?.data

  const handleReserveClick = (unit) => {
    if (!user) {
      navigate('/signin')
      return
    }

    if (profile?.role !== 'buyer') {
      alert('Only buyers can reserve units')
      return
    }

    setSelectedUnit(unit)

    const hasDiscount = currentDiscount && unit.status === 'available' && unitEligibility.get(unit.id) !== false
    if (hasDiscount) {
      const discountAmount = (unit.price * parseFloat(currentDiscount.discount_percent)) / 100
      const isFirstHomeBuyer = profile?.buyer_category === 'first_home'

      if (isFirstHomeBuyer) {
        const depositContribution = Math.min(10000, discountAmount)
        setDiscountAllocation({
          depositContribution: depositContribution,
          furniturePackages: 0,
          priceReduction: discountAmount - depositContribution,
        })
      } else {
        setDiscountAllocation({
          depositContribution: 0,
          furniturePackages: 0,
          priceReduction: discountAmount,
        })
      }
    } else {
      setDiscountAllocation({
        depositContribution: 0,
        furniturePackages: 0,
        priceReduction: 0,
      })
    }

    setShowReserveModal(true)
  }

  const handleReserveConfirm = () => {
    if (selectedUnit) {
      const hasDiscount = currentDiscount && selectedUnit.status === 'available' && unitEligibility.get(selectedUnit.id) !== false

      reserveMutation.mutate({
        unitId: selectedUnit.id,
        notes: reservationNotes,
        allocation: hasDiscount ? discountAllocation : null,
      })
    }
  }

  const handleFollowToggle = (e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    if (!user) {
      navigate('/signin')
      return
    }

    if (isFollowing?.data) {
      unfollowMutation.mutate()
    } else {
      followMutation.mutate()
    }
  }

  if (isLoading || imagesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner spinner-lg text-primary-600 mb-4"></div>
          <p className="text-secondary-600">Loading project...</p>
        </div>
      </div>
    )
  }

  if (error || !project) {
    return (
      <div className="container-custom py-12">
        <div className="alert-error">
          <p>Failed to load project. Please try again.</p>
        </div>
        <Link to="/projects" className="btn-primary mt-4">
          Back to Projects
        </Link>
      </div>
    )
  }

  // Combine project_images table with fallback to project.image_urls
  let projectImages = []

  // First try project_images table - normalize field names
  if (projectImagesData && projectImagesData.length > 0) {
    projectImages = projectImagesData.map(img => ({
      url: img.image_url,
      description: img.description,
      image_type: img.image_type,
      display_order: img.display_order
    }))
  }
  // Fall back to project.image_urls if no project_images
  else if (project?.image_urls && Array.isArray(project.image_urls) && project.image_urls.length > 0) {
    projectImages = project.image_urls.map((url, index) => ({
      url: url,
      description: project.name,
      image_type: 'render',
      display_order: index
    }))
  }

  // Filter for render images, or use all if no render images
  const renderImages = projectImages.filter(img => img.image_type === 'render' || !img.image_type)
  const mobileImages = renderImages.length > 0 ? renderImages : projectImages

  return (
    <div className="bg-secondary-50 min-h-screen">
      {/* Hero Image Carousel */}
      {/* Mobile Gallery with Instagram-style Heart */}
      <div className="md:hidden relative">
        <SwipeableImageGallery
          images={mobileImages.map(img => ({
            url: img?.url || img,
            alt: img?.description || project.name
          }))}
          aspectRatio="4/3"
        />
        {/* Bottom Right Badges - Stacked Vertically */}
        {user && profile?.role === 'buyer' && (
          <div className="absolute bottom-4 right-4 z-10 flex flex-col items-end gap-2">
            {/* Instagram-style Discount Badge - Above */}
            {currentDiscount && (
              <DiscountBadge
                discountPercent={parseFloat(currentDiscount.discount_percent)}
                size="md"
              />
            )}
            {/* Instagram-style Follow Button - Below */}
            <ProjectFollowButton
              isFollowing={isFollowing?.data}
              onClick={handleFollowToggle}
              size="md"
            />
          </div>
        )}
      </div>

      {/* Desktop Carousel */}
      <div className="hidden md:block h-96 md:h-[32rem] lg:h-[40rem] bg-secondary-200 overflow-hidden relative">
        <ImageCarousel images={projectImages} alt={project.name} showDescriptions={true} />
        {/* Bottom Right Badges for Desktop - Stacked Vertically */}
        {user && profile?.role === 'buyer' && (
          <div className="absolute bottom-8 right-8 z-10 flex flex-col items-end gap-3">
            {/* Instagram-style Discount Badge - Above */}
            {currentDiscount && (
              <DiscountBadge
                discountPercent={parseFloat(currentDiscount.discount_percent)}
                size="lg"
              />
            )}
            {/* Instagram-style Follow Button - Below */}
            <ProjectFollowButton
              isFollowing={isFollowing?.data}
              onClick={handleFollowToggle}
              size="lg"
            />
          </div>
        )}
      </div>

      <div className="container-custom py-4 sm:py-8">
        {/* Breadcrumb */}
        <nav className="mb-4 sm:mb-6 text-sm px-4 sm:px-0">
          <Link to="/projects" className="text-primary-600 hover:text-primary-700">
            Projects
          </Link>
          <span className="mx-2 text-secondary-400">/</span>
          <span className="text-secondary-600">{project.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 sm:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-0 sm:space-y-6">
            {/* Discount Banner for Buyers */}
            {user && profile?.role === 'buyer' && project.status === 'activated' && (
              <DiscountBanner projectId={projectId} />
            )}

            {/* Project Header */}
            <div className="card p-4 sm:p-6">
              <h1 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
                {project.name}
              </h1>

              {project.price_range_min && project.price_range_max && (
                <div className="mb-4">
                  <div className="text-2xl font-bold text-primary-600">
                    ${project.price_range_min.toLocaleString()} - ${project.price_range_max.toLocaleString()}
                  </div>
                  {currentDiscount && project.status === 'activated' && (
                    <div className="mt-2 inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                      <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-green-900">
                          Up to {parseFloat(currentDiscount.discount_percent).toFixed(1)}% discount available on selected units
                        </span>
                        <span className="text-xs text-green-700">
                          Limited time offer. Subject to change and unit availability.
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {project.description && (
                <p className="text-secondary-700 leading-relaxed">
                  {project.description}
                </p>
              )}
            </div>

            {/* Map */}
            <ProjectMap projectId={projectId} location={project.location} />

            {/* Project Timeline */}
            <ProjectTimeline projectId={projectId} projectCreatedAt={project.created_at} />

            {/* Q&A Section */}
            <div className="card p-4 sm:p-6">
              <ProjectQA projectId={projectId} isDeveloper={false} />
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="card p-4 sm:p-6 sticky top-20 space-y-4 sm:space-y-6">
              {/* Units */}
              <div>
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="text-lg font-semibold text-secondary-900">
                        Units Available for Sale
                      </h3>
                      <p className="text-xs text-secondary-600">
                        Reserve a unit to begin your purchase journey
                      </p>
                    </div>

                    {project.units && project.units.length > 0 && (
                      <div className="flex gap-1">
                        <button
                          onClick={() => setStatusFilter('all')}
                          className={`px-2 py-1 text-xs rounded transition-colors ${
                            statusFilter === 'all'
                              ? 'bg-primary-600 text-white'
                              : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                          }`}
                        >
                          All
                        </button>
                        <button
                          onClick={() => setStatusFilter('available')}
                          className={`px-2 py-1 text-xs rounded transition-colors ${
                            statusFilter === 'available'
                              ? 'bg-primary-600 text-white'
                              : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                          }`}
                        >
                          Available
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {!project.units || project.units.length === 0 ? (
                  <div className="text-center py-6">
                    <p className="text-sm text-secondary-500">No units available yet.</p>
                  </div>
                ) : (() => {
                  const filteredUnits = project.units.filter(
                    (unit) => statusFilter === 'all' || unit.status === statusFilter
                  )

                  return filteredUnits.length === 0 ? (
                    <div className="text-center py-6">
                      <p className="text-sm text-secondary-500">No units match the selected filter.</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                      {filteredUnits.map((unit) => {
                        const isReservedByMe = reservedUnitIds.has(unit.id)

                        return (
                          <Link
                            key={unit.id}
                            to={`/projects/${projectId}/units/${unit.id}`}
                            className={`block border rounded-lg p-3 transition-all hover:shadow-md ${
                              isReservedByMe
                                ? 'border-green-300 bg-green-50'
                                : 'border-secondary-200 hover:border-primary-400'
                            }`}
                          >
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <h4 className="font-semibold text-secondary-900 text-sm">
                                  Unit {unit.unit_number}
                                </h4>
                                {isReservedByMe && (
                                  <span className="badge bg-green-600 text-white text-xs">
                                    YOURS
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 text-xs text-secondary-600">
                                <div className="flex items-center gap-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 20v-8a2 2 0 012-2h14a2 2 0 012 2v8m-18 0h18M5 10V6a2 2 0 012-2h10a2 2 0 012 2v4M7 14h2m4 0h2" />
                                  </svg>
                                  <span className="font-medium">{unit.bedrooms}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 8V6a2 2 0 012-2h8a2 2 0 012 2v2m-4 4h.01M4 16h16a1 1 0 001-1V8H3v7a1 1 0 001 1zm0 0v2a2 2 0 002 2h1m-3-4v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
                                  </svg>
                                  <span className="font-medium">{unit.bathrooms}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                  </svg>
                                  <span className="font-medium">{unit.sqm}</span>
                                </div>
                              </div>
                              {unit.price && (
                                <div className="space-y-1">
                                  {currentDiscount &&
                                   unit.status === 'available' &&
                                   unitEligibility.get(unit.id) !== false ? (
                                    <>
                                      <div className="flex flex-col gap-1">
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs text-secondary-400 line-through">
                                            ${unit.price.toLocaleString('en-NZ')}
                                          </span>
                                          <div className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-100 text-green-800 rounded text-xs font-semibold">
                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                            <span>{parseFloat(currentDiscount.discount_percent).toFixed(1)}%</span>
                                          </div>
                                        </div>
                                        <div className="text-base font-bold text-green-700">
                                          ${(unit.price - (unit.price * parseFloat(currentDiscount.discount_percent)) / 100).toLocaleString('en-NZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                        </div>
                                        <div className="text-xs text-green-600">
                                          Save ${((unit.price * parseFloat(currentDiscount.discount_percent)) / 100).toLocaleString('en-NZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                        </div>
                                      </div>
                                    </>
                                  ) : (
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm text-secondary-500">Price:</span>
                                      <span className="text-base font-bold text-secondary-900">
                                        ${unit.price.toLocaleString('en-NZ')}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                              <div className="pt-2 flex items-center justify-between text-xs text-primary-600">
                                <span>View Details</span>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </div>
                            </div>
                          </Link>
                        )
                      })}
                    </div>
                  )
                })()}
              </div>

              {/* Developer Info */}
              <div className="border-t border-secondary-200 pt-6">
                <h3 className="text-lg font-semibold text-secondary-900 mb-3">Developer</h3>
                <Link
                  to={`/developers/${project.developer?.id}`}
                  className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium transition-colors group"
                >
                  <span>{project.developer?.company_name || project.developer?.full_name}</span>
                  <svg className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>

              {user && profile?.role === 'buyer' && !isFollowing?.data && (
                <div className="border-t border-secondary-200 pt-6">
                  <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="text-sm">
                        <p className="text-primary-900 font-medium mb-1">Like this project to stay updated</p>
                        <p className="text-primary-700">Get regular updates and be notified when it goes on the market so you can secure the best discount.</p>
                        <button
                          onClick={handleFollowToggle}
                          className="mt-2 text-primary-600 hover:text-primary-700 font-semibold underline"
                        >
                          Follow this project
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!user && (
                <div className="border-t border-secondary-200 pt-6">
                  <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="text-sm">
                        <p className="text-primary-900 font-medium mb-1">Sign in to follow this project</p>
                        <p className="text-primary-700">Get regular updates and be the first to know when it goes on the market to secure the best discounts.</p>
                      </div>
                    </div>
                  </div>
                  <Link to="/signin" className="btn-primary w-full text-center">
                    Sign in to Follow
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reserve Modal */}
      <BottomSheet
        isOpen={showReserveModal && selectedUnit}
        onClose={() => {
          setShowReserveModal(false)
          setReservationNotes('')
          setDiscountAllocation({
            priceReduction: 0,
            depositContribution: 0,
            furniturePackages: 0,
          })
        }}
        height="full"
      >
        {selectedUnit && (
          <>
            <BottomSheetHeader
              title={`Reserve Unit ${selectedUnit.unit_number}`}
              onClose={() => {
                setShowReserveModal(false)
                setReservationNotes('')
                setDiscountAllocation({
                  priceReduction: 0,
                  depositContribution: 0,
                  furniturePackages: 0,
                })
              }}
            />
            <div className="p-4 pb-8">
              {/* Reservation Duration Info */}
            <div className={`mb-4 p-4 rounded-lg border ${hasWithdrawals ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
              <div className="flex items-start gap-3">
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${hasWithdrawals ? 'bg-amber-100' : 'bg-blue-100'}`}>
                  <svg className={`w-5 h-5 ${hasWithdrawals ? 'text-amber-600' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-lg font-bold ${hasWithdrawals ? 'text-amber-900' : 'text-blue-900'}`}>
                      {reservationDuration} Hour Reservation
                    </span>
                    {hasWithdrawals && (
                      <span className="text-xs px-2 py-0.5 bg-amber-200 text-amber-800 rounded-full font-medium">
                        Reduced
                      </span>
                    )}
                  </div>
                  <p className={`text-sm ${hasWithdrawals ? 'text-amber-700' : 'text-blue-700'}`}>
                    {hasWithdrawals
                      ? 'Because you previously withdrew a reservation, your hold time is reduced to 48 hours.'
                      : 'You have 3 days to review and proceed to contract. After that, your reservation expires automatically.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Consequences Warning */}
            <div className="mb-4 p-3 bg-secondary-100 border border-secondary-200 rounded-lg">
              <p className="text-xs text-secondary-600 leading-relaxed">
                <span className="font-semibold text-secondary-700">Important:</span> If you withdraw or let this reservation expire without proceeding to contract,{' '}
                {hasWithdrawals
                  ? 'your future reservations will remain at 48 hours.'
                  : <span className="text-amber-700 font-medium">any future reservations will only be for 48 hours instead of 72 hours.</span>}
                {' '}We recommend only reserving when you are serious about purchasing.
              </p>
            </div>

            <div className="mb-4 p-4 bg-secondary-50 rounded-lg text-sm">
              <div className="grid grid-cols-2 gap-2 text-secondary-700 mb-3">
                <div><span className="font-medium">Bedrooms:</span> {selectedUnit.bedrooms}</div>
                <div><span className="font-medium">Bathrooms:</span> {selectedUnit.bathrooms}</div>
                <div><span className="font-medium">Size:</span> {selectedUnit.sqm} sqm</div>
                {selectedUnit.price && (
                  <div className="col-span-2">
                    <span className="font-medium">Original Price:</span> ${selectedUnit.price.toLocaleString('en-NZ')}
                  </div>
                )}
              </div>
              {currentDiscount && unitEligibility.get(selectedUnit.id) !== false && (() => {
                const discountAmount = (selectedUnit.price * parseFloat(currentDiscount.discount_percent)) / 100
                const isFirstHomeBuyer = profile?.buyer_category === 'first_home'
                const finalPrice = selectedUnit.price - discountAmount

                return (
                  <div className="mt-3 pt-3 border-t border-secondary-300">
                    <div className="bg-green-50 border border-green-200 rounded-md p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="font-semibold text-green-900">
                          {parseFloat(currentDiscount.discount_percent).toFixed(1)}% Early Buyer Discount
                        </span>
                      </div>

                      <div className="space-y-3">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-green-800">Total Savings</span>
                          <span className="font-bold text-green-900 text-lg">
                            ${discountAmount.toLocaleString('en-NZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </span>
                        </div>

                        <div className="border-t border-green-200 pt-3 space-y-2">
                          <p className="text-xs font-medium text-green-800 uppercase tracking-wide">Your discount will be applied as:</p>

                          {isFirstHomeBuyer && discountAllocation.depositContribution > 0 && (
                            <div className="flex justify-between items-center text-sm bg-white/50 rounded px-2 py-1.5">
                              <span className="text-green-700">Deposit contribution</span>
                              <span className="font-semibold text-green-900">
                                ${discountAllocation.depositContribution.toLocaleString('en-NZ')}
                              </span>
                            </div>
                          )}

                          <div className="flex justify-between items-center text-sm bg-white/50 rounded px-2 py-1.5">
                            <span className="text-green-700">Price reduction</span>
                            <span className="font-semibold text-green-900">
                              ${discountAllocation.priceReduction.toLocaleString('en-NZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </span>
                          </div>
                        </div>

                        <div className="border-t border-green-200 pt-3">
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-green-900">Your Price</span>
                            <span className="font-bold text-green-900 text-xl">
                              ${finalPrice.toLocaleString('en-NZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </span>
                          </div>
                        </div>

                        {isFirstHomeBuyer && (
                          <p className="text-xs text-green-700 bg-green-100 rounded p-2 mt-2">
                            As a first home buyer, $10,000 of your discount goes towards your deposit to help with your home loan.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })()}
            </div>

            <div className="form-group mb-6">
              <label className="label">Notes (Optional)</label>
              <textarea
                value={reservationNotes}
                onChange={(e) => setReservationNotes(e.target.value)}
                className="textarea"
                placeholder="Add any notes or questions about this unit..."
                rows={3}
              />
            </div>

            {reserveMutation.error && (
              <div className="alert-error mb-4">
                <p>{reserveMutation.error.message}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowReserveModal(false)
                  setReservationNotes('')
                  setDiscountAllocation({
                    priceReduction: 0,
                    depositContribution: 0,
                    furniturePackages: 0,
                  })
                }}
                className="btn-outline flex-1"
                disabled={reserveMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={handleReserveConfirm}
                className="btn-primary flex-1"
                disabled={reserveMutation.isPending}
              >
                {reserveMutation.isPending ? 'Reserving...' : 'Confirm Reservation'}
              </button>
            </div>
            </div>
          </>
        )}
      </BottomSheet>
    </div>
  )
}
